import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
  ElementRef
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Subscription } from 'rxjs';
import { AccordionDataService, AccordionFolder } from '../../services/accordion-data.service';
import {
  SharedUsersDialogComponent,
  SharedUsersDialogData
} from '../shared-users-dialog/shared-users-dialog.component';

// Interfaces
interface SpokeHub {
  name: string;
  facility?: string;
  facilityId?: string;
  facilityName?: string;
}

interface FolderFile {
  fileId: string;
  fileName: string;
  fileDesc?: string;
  fdate?: string;
  ftime?: string;
  lastModified?: string;
  uploadedDate?: string;
  type?: string;
  parentFolderName?: string;
  parentFolder?: string;
  sharedUsers?: any[];
  storename?: string;
  viewerurl?: string;
  hasNotifications?: boolean;
  hasCommentAttachments?: boolean;
  color?: string;
  fileSize?: number;
}

interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

const DICOMDIR_FILE_NAME = 'DICOMDIR';

@Component({
    selector: 'app-home-folders-accordion-spokes',
    templateUrl: './home-folders-accordion-spokes.component.html',
    styleUrls: ['./home-folders-accordion-spokes.component.scss'],
    standalone: false
})
export class HomeFoldersAccordionSpokesComponent implements OnInit, OnDestroy, OnChanges {
  // Simplified inputs for SPOKE-only functionality
  @Input() permissions: any = { createfolder: false, deletefiles: false, editfiles: false, sharefileicon: false };
  @Input() loading: boolean = false;
  @Input() loadingMessage: string = '';
  @Input() casename: string = '';
  @Input() selectedSpoke: any = null;
  @Input() isHub: boolean = false;
  @Input() showSpokesAsFolders: boolean = false;
  @Input() sharerSpokes: SpokeHub[] = [];
  @Input() hubName: string = 'John Dell Hospital'; // Dynamic hub name for title

  // Essential outputs for SPOKE functionality
  @Output() spokeClick = new EventEmitter<any>();
  @Output() loadFolderContents = new EventEmitter<string>();
  @Output() refreshFolderContents = new EventEmitter<string>();
  @Output() fileClick = new EventEmitter<FolderFile>();
  @Output() editFile = new EventEmitter<{ file: FolderFile; event: Event }>();
  @Output() deleteFile = new EventEmitter<{ file: FolderFile; event: Event }>();
  @Output() copyFile = new EventEmitter<{ file: FolderFile; event: Event }>();
  @Output() generateDisc = new EventEmitter<{ file: FolderFile; event: Event }>();
  @Output() addCommentOrAttachment = new EventEmitter<{ file: FolderFile; event: Event }>();
  @Output() sharedIconClick = new EventEmitter<FolderFile>();
  @Output() folderNameClick = new EventEmitter<FolderFile>();
  @Output() studyClick = new EventEmitter<FolderFile>();
  @Output() folderSelectionChange = new EventEmitter<{ folderId: string; selectedFiles: FolderFile[] }>();
  @Output() allFoldersClosed = new EventEmitter<void>();

  accordionFolders: AccordionFolder[] = [];
  private subscription: Subscription = new Subscription();

  // New property to track hierarchical display
  displayFolders: AccordionFolder[] = [];

  // ViewChildren for folder elements to enable scrolling
  @ViewChildren('folderItem') folderItems!: QueryList<ElementRef>;

  // File selection tracking for each folder
  private folderSelections: Map<string, Set<string>> = new Map();

  // Pagination properties
  private paginationStates: Map<string, PaginationState> = new Map();
  readonly defaultPageSize = 5;
  readonly maxPageSize = 50;

  // Tab selection tracking for spoke folders
  private tabSelectionStates: Map<string, number> = new Map();

  // Make Math available in template
  Math = Math;

  constructor(
    private accordionDataService: AccordionDataService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Debug logging
    console.log('Spokes Component - Is Hub:', this.isHub);
    console.log('Spokes Component - Show Spokes as Folders:', this.showSpokesAsFolders);
    console.log('Spokes Component - Sharer Spokes:', this.sharerSpokes);
    
    // Initialize ONLY SPOKE folders for hub users - manage independently
    if (this.isHub && this.showSpokesAsFolders) {
      console.log('Initializing spoke folders...');
      this.initializeSpokeFolders();
      // Set display folders directly from our local spoke folders
      this.setDisplayFoldersDirectly();
    }
  }

  /**
   * Initialize SPOKE folders for hub users
   */
  private initializeSpokeFolders(): void {
    // Use provided sharerSpokes or default mock data
    const spokesToUse = this.sharerSpokes && this.sharerSpokes.length > 0 
      ? this.sharerSpokes 
      : [
          { name: 'Springfield General Hospital', facilityId: 'spoke-1', facility: 'Springfield General' },
          { name: 'Riverside Medical Center', facilityId: 'spoke-2', facility: 'Riverside Medical' },
          { name: 'City Hospital', facilityId: 'spoke-3', facility: 'City Hospital' }
        ];

    const spokeFolders = spokesToUse.map((spoke, index) => ({
      id: `spoke-${index}`,
      name: typeof spoke === 'string' ? spoke : spoke.name || spoke.facilityName || `Spoke ${index + 1}`,
      color: '#4CAF50', // Green color for spoke folders
      hasNotifications: false,
      count: '',
      type: 'spoke',
      expanded: false,
      loading: false,
      refreshing: false,
      files: null,
      spokeData: spoke, // Store the original spoke data
      restrictedTo: null
    }));

    console.log('Created spoke folders:', spokeFolders);
    // Store spoke folders locally instead of using the shared service
    this.accordionFolders = spokeFolders;
    this.initializePaginationForFolders(spokeFolders);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Watch for changes to the selectedSpoke
    if (changes['selectedSpoke']) {
      // When spoke changes, re-apply any filtering if needed
      this.setDisplayFoldersDirectly();

      // For hub users with showSpokesAsFolders, also re-initialize if we don't have spoke folders yet
      if (this.isHub && this.showSpokesAsFolders) {
        const hasSpokeFolders = this.accordionFolders && this.accordionFolders.length > 0;
        if (!hasSpokeFolders) {
          this.initializeSpokeFolders();
          this.setDisplayFoldersDirectly();
        }
      }
    }

    // Watch for changes to sharerSpokes (for hub users with showSpokesAsFolders)
    if (changes['sharerSpokes'] && this.isHub && this.showSpokesAsFolders) {
      this.initializeSpokeFolders();
      this.setDisplayFoldersDirectly();
    }

    // Watch for changes to showSpokesAsFolders
    if (changes['showSpokesAsFolders']) {
      this.initializeSpokeFolders();
      this.setDisplayFoldersDirectly();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Ensure spoke folders are present in the accordion data service
   */
  private spokeFolderStates: Map<string, { expanded: boolean; files: any[] }> = new Map();

  private ensureSpokeFoldersPresent(): void {
    if (this.isHub && this.showSpokesAsFolders) {
      const hasSpokeFolders = this.accordionFolders && this.accordionFolders.length > 0;

      // Save current spoke folder states before potentially recreating them
      if (hasSpokeFolders) {
        this.accordionFolders.forEach(spoke => {
          this.spokeFolderStates.set(spoke.id, {
            expanded: spoke.expanded || false,
            files: spoke.files || []
          });
        });
      }

      if (!hasSpokeFolders) {
        // Re-initialize spoke folders if we don't have any
        this.initializeSpokeFolders();
        this.setDisplayFoldersDirectly();
      }
    }
  }

  private updateDisplayFolders(): void {
    const allFolders = this.accordionDataService.getCurrentFolders();
    console.log('All folders from service:', allFolders);
    // Always show only spoke folders - this component should only display spokes
    const foldersToShow = allFolders.filter(folder => folder.type === 'spoke');
    console.log('Filtered spoke folders:', foldersToShow);
    this.displayFolders = foldersToShow;
    console.log('Display folders set to:', this.displayFolders);
  }

  /**
   * Set display folders directly from local spoke folders (independent of shared service)
   */
  private setDisplayFoldersDirectly(): void {
    console.log('Setting display folders directly from local spoke folders:', this.accordionFolders);
    this.displayFolders = [...this.accordionFolders];
    console.log('Display folders set to:', this.displayFolders);
  }

  // Pagination methods
  private initializePaginationForFolders(folders: AccordionFolder[]): void {
    folders.forEach(folder => {
      if (!this.paginationStates.has(folder.id)) {
        this.paginationStates.set(folder.id, {
          currentPage: 1,
          pageSize: this.defaultPageSize,
          totalItems: 0,
          totalPages: 0
        });
      }
    });
  }

  private updatePaginationState(folderId: string, totalItems: number): void {
    const state = this.paginationStates.get(folderId) || {
      currentPage: 1,
      pageSize: this.defaultPageSize,
      totalItems: 0,
      totalPages: 0
    };

    state.totalItems = totalItems;
    state.totalPages = Math.ceil(totalItems / state.pageSize);

    if (state.currentPage > state.totalPages && state.totalPages > 0) {
      state.currentPage = state.totalPages;
    }

    this.paginationStates.set(folderId, state);
  }

  getPaginationState(folderId: string): PaginationState {
    return (
      this.paginationStates.get(folderId) || {
        currentPage: 1,
        pageSize: this.defaultPageSize,
        totalItems: 0,
        totalPages: 0
      }
    );
  }

  getPaginatedFiles(folder: AccordionFolder): FolderFile[] {
    if (!folder.files || folder.files.length === 0) {
      return [];
    }

    const state = this.getPaginationState(folder.id);
    const startIndex = (state.currentPage - 1) * state.pageSize;
    const endIndex = startIndex + state.pageSize;

    return folder.files.slice(startIndex, endIndex);
  }

  onPageChange(folderId: string, page: number): void {
    const state = this.paginationStates.get(folderId);
    if (state && page >= 1 && page <= state.totalPages) {
      state.currentPage = page;
      this.paginationStates.set(folderId, state);
    }
  }

  onPageSizeChange(folderId: string, pageSize: number): void {
    const state = this.paginationStates.get(folderId);
    if (state) {
      state.pageSize = Math.min(Math.max(pageSize, 10), this.maxPageSize);
      state.currentPage = 1;
      state.totalPages = Math.ceil(state.totalItems / state.pageSize);
      this.paginationStates.set(folderId, state);
    }
  }

  getPageSizeOptions(): number[] {
    return [5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
  }

  /**
   * Handle folder expansion from mat-expansion-panel (opened) event
   */
  handleFolderExpansion(folder: AccordionFolder): void {
    // Create a mock event for compatibility with existing methods
    const mockEvent = new Event('click');
    this.onFolderClick(folder, mockEvent);
  }

  onFolderClick(folder: AccordionFolder, event: Event): void {
    event.stopPropagation();

    // Handle spoke folders differently
    if (folder.type === 'spoke') {
      this.handleSpokeFolderClick(folder, event);
      return;
    }
  }

  /**
   * Handle spoke folder click - emit spoke selection event and implement accordion behavior
   */
  private handleSpokeFolderClick(folder: AccordionFolder, event: Event): void {
    const isCurrentlySelected = this.isSpokeSelected(folder);

    if (isCurrentlySelected) {
      // If clicking on the already selected spoke, deselect it
      folder.expanded = false;

      // Clear the saved state for this spoke
      this.spokeFolderStates.set(folder.id, {
        expanded: false,
        files: []
      });

      this.setDisplayFoldersDirectly();
      // Emit null to indicate no spoke is selected
      this.spokeClick.emit(null);
    } else {
      // For spoke folders, we want accordion behavior - only one spoke can be selected at a time
      // First, close all other spoke folders
      this.closeAllSpokeFolders();

      // Then expand the clicked spoke folder
      folder.expanded = true;

      // Save the expanded state immediately
      this.spokeFolderStates.set(folder.id, {
        expanded: true,
        files: folder.files || []
      });

      // Update display folders to reflect the new expansion state
      this.setDisplayFoldersDirectly();

      // Load mock files for this spoke
      this.loadMockFilesForSpoke(folder);

      // Emit spoke click event to parent component
      this.spokeClick.emit(folder.spokeData);
    }
  }

  /**
   * Load mock files for a spoke folder (to be replaced with real data later)
   */
  private loadMockFilesForSpoke(folder: AccordionFolder): void {
    // Set loading state
    folder.loading = true;

    // Simulate API call with timeout
    setTimeout(() => {
      // Mock files data
      const mockFiles: FolderFile[] = [
        {
          fileId: `file-${folder.id}-1`,
          fileName: `Patient Record 001.pdf`,
          fileDesc: 'Patient medical records',
          fdate: '2024-01-15',
          lastModified: '2024-01-15',
          uploadedDate: '2024-01-15',
          type: 'file',
          parentFolderName: folder.name,
          sharedUsers: [],
          hasNotifications: false,
          color: folder.color,
          fileSize: 2048576 // 2MB
        },
        {
          fileId: `file-${folder.id}-2`,
          fileName: `Lab Results.pdf`,
          fileDesc: 'Laboratory test results',
          fdate: '2024-01-16',
          lastModified: '2024-01-16',
          uploadedDate: '2024-01-16',
          type: 'file',
          parentFolderName: folder.name,
          sharedUsers: [],
          hasNotifications: false,
          color: folder.color,
          fileSize: 1024768 // 1MB
        },
        {
          fileId: `file-${folder.id}-3`,
          fileName: `DICOMDIR`,
          fileDesc: 'Medical imaging study',
          fdate: '2024-01-17',
          lastModified: '2024-01-17',
          uploadedDate: '2024-01-17',
          type: 'dicom',
          parentFolderName: folder.name,
          sharedUsers: [],
          hasNotifications: false,
          viewerurl: '/sample/viewer/url',
          color: folder.color,
          fileSize: 52428800 // 50MB
        }
      ];

      // Update folder with mock files locally
      folder.files = mockFiles;
      folder.loading = false;
      this.updatePaginationState(folder.id, mockFiles.length);
    }, 500);
  }

  /**
   * Close all spoke folders to implement accordion behavior
   */
  private closeAllSpokeFolders(): void {
    this.accordionFolders.forEach(spokeFolder => {
      if (spokeFolder.expanded) {
        spokeFolder.expanded = false;
      }
    });
  }

  /**
   * Check if a spoke folder is currently selected (expanded)
   */
  isSpokeSelected(folder: AccordionFolder): boolean {
    if (folder.type !== 'spoke') {
      return false;
    }

    return this.isExpanded(folder);
  }

  /**
   * Get the currently selected spoke folder
   */
  getSelectedSpokeFolder(): AccordionFolder | null {
    return this.accordionFolders.find(spokeFolder => spokeFolder.expanded) || null;
  }

  onRefreshFolder(folder: AccordionFolder, event: Event): void {
    event.stopPropagation();

    // Set refreshing state
    folder.refreshing = true;

    // Update display folders to reflect the refreshing state
    this.setDisplayFoldersDirectly();

    // Emit refresh event to parent component
    this.refreshFolderContents.emit(folder.id);
  }

  getFolderIcon(folder: AccordionFolder): string {
    if (folder.type === 'spoke') {
      return 'business';
    }
    return 'folder';
  }

  getFolderColor(folder: AccordionFolder): string {
    return folder.color || '#757575';
  }

  getFolderDisplayName(folder: AccordionFolder): string {
    return folder.name;
  }

  isExpanded(folder: AccordionFolder): boolean {
    if (folder.expanded !== undefined) {
      return folder.expanded;
    }

    const currentFolder = this.accordionFolders.find(f => f.id === folder.id);
    return currentFolder ? currentFolder.expanded || false : false;
  }

  areAllFoldersClosed(): boolean {
    return this.accordionFolders.every(folder => !folder.expanded);
  }

  checkAndEmitAllFoldersClosed(): void {
    if (this.areAllFoldersClosed()) {
      this.allFoldersClosed.emit();
    }
  }

  isRefreshing(folder: AccordionFolder): boolean {
    return folder.refreshing || false;
  }

  onFileClick(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.fileClick.emit(file);
  }

  // File selection methods
  isFileSelected(folder: AccordionFolder, file: FolderFile): boolean {
    const selection = this.folderSelections.get(folder.id);
    return selection ? selection.has(file.fileId) : false;
  }

  toggleFileSelection(folder: AccordionFolder, file: FolderFile, event: MatCheckboxChange): void {
    let selection = this.folderSelections.get(folder.id);
    if (!selection) {
      selection = new Set();
      this.folderSelections.set(folder.id, selection);
    }

    if (event.checked) {
      selection.add(file.fileId);
    } else {
      selection.delete(file.fileId);
    }

    // Emit selection change
    this.folderSelectionChange.emit({
      folderId: folder.id,
      selectedFiles: this.getSelectedFiles(folder.id)
    });
  }

  isAllSelected(folder: AccordionFolder): boolean {
    if (!folder.files || folder.files.length === 0) return false;
    const selection = this.folderSelections.get(folder.id);
    return selection ? selection.size === folder.files.length : false;
  }

  isPartiallySelected(folder: AccordionFolder): boolean {
    if (!folder.files || folder.files.length === 0) return false;
    const selection = this.folderSelections.get(folder.id);
    if (!selection) return false;
    return selection.size > 0 && selection.size < folder.files.length;
  }

  masterToggle(folder: AccordionFolder, event: MatCheckboxChange): void {
    if (!folder.files || folder.files.length === 0) return;

    let selection = this.folderSelections.get(folder.id);
    if (!selection) {
      selection = new Set();
      this.folderSelections.set(folder.id, selection);
    }

    if (event.checked) {
      // Select all
      folder.files.forEach(file => selection.add(file.fileId));
    } else {
      // Deselect all
      selection.clear();
    }

    // Emit selection change
    this.folderSelectionChange.emit({
      folderId: folder.id,
      selectedFiles: this.getSelectedFiles(folder.id)
    });
  }

  onEditFile(file: FolderFile, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.editFile.emit({ file, event });
  }

  onDeleteFile(file: FolderFile, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.deleteFile.emit({ file, event });
  }

  onCopyFile(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.copyFile.emit({ file, event });
  }

  onGenerateDisc(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.generateDisc.emit({ file, event });
  }

  onAddCommentOrAttachment(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.addCommentOrAttachment.emit({ file, event });
  }

  onSharedIconClick(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.sharedIconClick.emit(file);
  }

  onFolderNameClick(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.folderNameClick.emit(file);
  }

  isDicomFile(file: FolderFile): boolean {
    return file.fileName === DICOMDIR_FILE_NAME || file.type === 'dicom';
  }

  isInactiveStore(file: FolderFile): boolean {
    return file.storename === 'inactive' || file.storename === 'Inactive';
  }

  onStudyClick(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.studyClick.emit(file);
  }

  getSelectedFiles(folderId: string): FolderFile[] {
    const selection = this.folderSelections.get(folderId);
    if (!selection || selection.size === 0) return [];

    const folder = this.accordionFolders.find(f => f.id === folderId);
    if (!folder || !folder.files) return [];

    return folder.files.filter(file => selection.has(file.fileId));
  }

  clearSelection(folderId: string): void {
    this.folderSelections.delete(folderId);
  }

  clearAllSelections(): void {
    this.folderSelections.clear();
  }

  trackByFileId(index: number, file: FolderFile): string {
    return file.fileId || index.toString();
  }

  trackByFolderId(index: number, folder: AccordionFolder): string {
    return folder.id || index.toString();
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';

    try {
      let date: Date;

      if (dateString.includes('GMT') || dateString.includes('UTC')) {
        date = new Date(dateString);
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
        date = new Date(dateString);
      } else if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) {
        date = new Date(dateString);
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) return '';

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return '';
    }
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return '';

    try {
      let date: Date;

      if (dateString.includes('GMT') || dateString.includes('UTC')) {
        date = new Date(dateString);
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
        date = new Date(dateString);
      } else if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) {
        date = new Date(dateString);
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) return '';

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '';
    }
  }

  formatFileDateTime(fdate: string, ftime: string): string {
    if (!fdate) return '';

    try {
      if (ftime) {
        const dateTimeString = `${fdate} ${ftime}`;
        const date = new Date(dateTimeString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      }

      const date = new Date(fdate);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }

      return '';
    } catch (error) {
      return '';
    }
  }
}
